import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { InputField, InputLabel, InputRadio } from '@presentation/components';
import type { SelectOption } from '@presentation/components';
import { useValidation } from '@application/utils';
import { useFormikContext } from 'formik';
import * as Styled from '../styled';
import type { MtBreederOnboardingFormValues } from '../types';

export const Step7: FC = () => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<MtBreederOnboardingFormValues>();
  const [hasDogsConsanguinity, setHasDogsConsanguinity] = useState<boolean | null>(null);
  const { requiredMessage } = useValidation();
  const { translate } = useTranslation();

  useEffect(() => {
    if (values?.dogsConsanguinity === NO_DOG_CONSANGUINITY) {
      setHasDogsConsanguinity(false);
    } else if (values?.dogsConsanguinity) {
      setHasDogsConsanguinity(true);
    } else {
      setHasDogsConsanguinity(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const NO_DOG_CONSANGUINITY = translate('glossary.no');

  const toggleConsanguinity = (option: SelectOption<boolean>) => {
    setHasDogsConsanguinity(option.value);
    if (option.value) {
      setFieldValue('dogsConsanguinity', null);
    } else {
      setFieldValue('dogsConsanguinity', NO_DOG_CONSANGUINITY);
    }
  };

  const yesNoOptions: SelectOption<boolean>[] = [
    { value: true, label: translate('enum.yesNoUnknownStatus.YES') },
    { value: false, label: translate('enum.yesNoUnknownStatus.NO') },
  ];

  const dogsConsanguinityHasError = useMemo(() => {
    return (
      hasDogsConsanguinity === null && !!errors.dogsConsanguinity && !!touched.dogsConsanguinity
    );
  }, [errors.dogsConsanguinity, hasDogsConsanguinity, touched.dogsConsanguinity]);
  return (
    <>
      <Styled.OnboardingInputWrapper>
        <InputLabel requiredColor="red" required>
          {translate('common.inputs.diseaseTesting.label').replaceAll('<br/>', '')}
        </InputLabel>
        <InputField name="medicalTestsPerformed" variant="new" required textarea />
      </Styled.OnboardingInputWrapper>
      <Styled.OnboardingInputWrapper>
        <InputLabel requiredColor="red" required>
          {translate('common.inputs.breedsConsanguinity.label').replaceAll('<br/>', '')}
        </InputLabel>
        <InputRadio<boolean>
          variant="blue"
          separated
          options={yesNoOptions}
          value={hasDogsConsanguinity}
          defaultChecked={false}
          onSelect={toggleConsanguinity}
          hasError={dogsConsanguinityHasError}
        />
        {dogsConsanguinityHasError ? <Styled.ErrorText>{requiredMessage}</Styled.ErrorText> : null}
      </Styled.OnboardingInputWrapper>

      {hasDogsConsanguinity && (
        <>
          <Styled.BlueInfoBox isFlex>{translate('common.inputs.step4Warn')}</Styled.BlueInfoBox>
          <Styled.OnboardingInputWrapper>
            <InputField name="dogsConsanguinity" required />
          </Styled.OnboardingInputWrapper>
        </>
      )}
    </>
  );
};
